import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import {
  dismissFollowUp,
  requeueFollowUp,
  updateActiveNote,
} from "@/features/leads/stores/followUpNotificationSlice";
import { setCurrentLeadId } from "@/features/leads/stores/leadFunctionalSlice";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useLeadMutations } from "@/features/leads/api/useLeadMutations";
import { useChildMutations } from "@/features/leads/api/useChildMutations";
import { toast } from "sonner";
import {
  X,
  Clock,
  Eye,
  Bell,
  ChevronRight,
  Phone,
  MessageCircle,
} from "lucide-react";
import { buildTelHref, buildWhatsAppUrl } from "@/constants/countryCodes";
import { useFollowUpTimeEnabled } from "@/features/leads/api/useLeadFormConfig";

/**
 * "Follow-up Due" popup.
 *
 * Fires off the server's `followup:due` websocket push, which the backend sweep
 * sends the minute a pending follow-up's date+time passes — to that follow-up's
 * personal-scope owners only. There is no client-side timer: see
 * `followUpNotificationSlice` for why the old local-scheduling version could
 * never work.
 *
 * Mounted in AppLayout (inside the router) so "View Lead" can navigate, same as
 * RecaptureAlertHost.
 */

/**
 * Postpone options.
 *
 * The hour-based ones only exist when the tenant captures a follow-up TIME:
 * with times off every follow-up is a whole DAY, so "+1 Hour" would resolve to
 * the same day the reminder is already on and Postpone would visibly do
 * nothing. Date-only tenants get day-sized moves instead.
 */
type PostponeOption = "+1h" | "+2h" | "tomorrow" | "+3d" | "+7d";

const TIMED_OPTIONS: PostponeOption[] = ["+1h", "+2h", "tomorrow"];
const DATE_ONLY_OPTIONS: PostponeOption[] = ["tomorrow", "+3d", "+7d"];

const POSTPONE_LABEL: Record<PostponeOption, string> = {
  "+1h": "+1 Hour",
  "+2h": "+2 Hours",
  tomorrow: "Tomorrow",
  "+3d": "In 3 days",
  "+7d": "Next week",
};

function getPostponedDate(
  option: PostponeOption,
  timeEnabled: boolean,
): string {
  const now = new Date();
  if (option === "+1h")
    return new Date(now.getTime() + 60 * 60 * 1000).toISOString();
  if (option === "+2h")
    return new Date(now.getTime() + 2 * 60 * 60 * 1000).toISOString();

  const days = option === "+3d" ? 3 : option === "+7d" ? 7 : 1;
  const target = new Date(now);
  target.setDate(target.getDate() + days);
  // The server flattens to 00:00 IST for a date-only tenant anyway
  // (`resolveFollowUpDate`); sending the day's start keeps the optimistic toast
  // honest about what was actually written.
  target.setHours(timeEnabled ? 10 : 0, 0, 0, 0);
  return target.toISOString();
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** "Follow-up in 10 min" when the tenant's "remind before" rang it early. */
function dueTitle(date: string): string {
  const mins = Math.round((new Date(date).getTime() - Date.now()) / 60_000);
  return mins > 0 ? `Follow-up in ${mins} min` : "Follow-up Due";
}

export default function FollowUpNotifier() {
  const dispatch = useAppDispatch();
  const queue = useAppSelector((s) => s.followUpNotification.queue);
  const active = useAppSelector((s) => s.followUpNotification.active);
  const [editingNote, setEditingNote] = useState(false);
  const [localNote, setLocalNote] = useState("");
  const followUpTimeEnabled = useFollowUpTimeEnabled();
  const { patchLeadSilently } = useLeadMutations();
  const { patchChildSilently } = useChildMutations();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (active) {
      setLocalNote(active.note);
      setEditingNote(false);
    }
  }, [active]);

  const saveNoteIfEditing = useCallback(() => {
    if (editingNote && localNote !== active?.note) {
      dispatch(updateActiveNote(localNote));
    }
  }, [dispatch, editingNote, localNote, active]);

  const handleDismiss = useCallback(() => {
    saveNoteIfEditing();
    dispatch(dismissFollowUp());
  }, [dispatch, saveNoteIfEditing]);

  /**
   * Postpone writes a NEW follow-up through the normal patch path, which closes
   * out the current one and starts a fresh Pending row — so the server sweep
   * rings again at the new time. (The previous version only pushed the item back
   * into a local array, which meant "Postpone" silently did nothing once the tab
   * reloaded.)
   *
   * A DEAL-level reminder must go to the CHILD endpoint. Deal follow-ups carry
   * `childId` with `leadId` NULL, and `noteService.createFollowUp(leadId,
   * "leads", …)` only closes rows whose `leadId` column matches — so patching
   * the lead would leave the deal's row Pending (it would ring again) AND create
   * an unrelated lead-level follow-up. The child path closes the deal's row and
   * already cascades to the parent lead's pending ones.
   */
  const handlePostpone = useCallback(
    (option: PostponeOption) => {
      if (!active) return;
      const newDate = getPostponedDate(option, followUpTimeEnabled);
      const note = editingNote ? localNote : active.note;
      const isDeal = active.childId != null;

      const vars = {
        type: "updateFollowup",
        id: (isDeal ? active.childId : active.leadId) as string | number,
        data: {
          followUp: {
            date: newDate,
            status: "Pending",
            completedBy: {},
            ...(note?.trim() ? { note: { note: note.trim() } } : {}),
          },
        },
      };
      const opts = {
        onSuccess: () =>
          toast.success(
            followUpTimeEnabled
              ? `Follow-up moved to ${formatTime(newDate)}, ${formatDate(newDate)}`
              : `Follow-up moved to ${formatDate(newDate)}`,
          ),
        onError: () => {
          toast.error("Couldn't postpone the follow-up — still due");
          // The server stamped `remindedAt` when it rang, so this reminder will
          // never fire again on its own. Dismissing optimistically and losing
          // the write would leave it silently overdue, so put the popup back and
          // let the user retry. `dismissed` is keyed on id, and pushDueFollowUp
          // skips anything in `seen` — so re-queue explicitly.
          dispatch(requeueFollowUp(dismissedItem));
        },
      };
      // Snapshot before the optimistic dismiss clears `active`.
      const dismissedItem = { ...active, note: note ?? active.note };

      if (isDeal) patchChildSilently.mutate(vars, opts);
      else patchLeadSilently.mutate(vars, opts);

      dispatch(dismissFollowUp());
    },
    [
      active,
      dispatch,
      editingNote,
      localNote,
      patchLeadSilently,
      patchChildSilently,
      followUpTimeEnabled,
    ],
  );

  /**
   * Open the lead the reminder is about. Same route as the recapture popup and
   * the notification inbox: set the current lead id, then land on /leads if we
   * aren't already there — the 16ms defer lets the dialog's close animation eat
   * the click tail before the lead sheet mounts (otherwise Radix closes it).
   */
  const handleViewLead = useCallback(() => {
    const leadId = active?.leadId;
    saveNoteIfEditing();
    dispatch(dismissFollowUp());
    if (leadId == null) return;
    window.setTimeout(() => {
      dispatch(setCurrentLeadId(leadId));
      if (!location.pathname.startsWith("/leads")) navigate("/leads");
    }, 16);
  }, [active, dispatch, location.pathname, navigate, saveNoteIfEditing]);

  // "+N more" = the follow-ups the server coalesced into this alert, plus any
  // later ticks still queued behind it. queue.length alone would read "+0 more"
  // for a user with 499 due at once, since the server sends them ONE event.
  const queueCount = (active?.alsoDueCount ?? 0) + queue.length;

  return (
    <>
      {active && (
        <Dialog
          open={!!active}
          onOpenChange={(open) => {
            if (!open) handleDismiss();
          }}
        >
          <DialogContent className="sm:max-w-[380px] p-0 rounded-[20px] border-[#e6e8e7] shadow-[0px_8px_40px_rgba(0,0,0,0.12)] gap-0 [&>button]:hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <div className="flex items-center gap-2">
                <div className="size-8 rounded-xl bg-[#eaf5f1] flex items-center justify-center">
                  <Bell className="size-3.5 text-[#218760]" />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <p className="text-[13px] font-medium tracking-[-0.26px] text-[#1e302a]">
                      {dueTitle(active.date)}
                    </p>
                    {queueCount > 0 && (
                      <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-[#FF8447] text-white">
                        +{queueCount} more
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] font-normal text-[#9ba3a0]">
                    {followUpTimeEnabled
                      ? `${formatTime(active.date)} · ${formatDate(active.date)}`
                      : formatDate(active.date)}
                  </p>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="size-7 rounded-full flex items-center justify-center hover:bg-[#f7f9f8] transition-colors"
              >
                <X className="size-3.5 text-[#697571]" />
              </button>
            </div>

            {/* Lead info */}
            <div className="mx-4 p-3 rounded-xl bg-[#f7f9f8] border border-[#e6e8e7]">
              <div className="flex items-center gap-2.5">
                <div className="size-9 rounded-full bg-[#eaf5f1] flex items-center justify-center text-[11px] font-medium text-[#218760] shrink-0">
                  {active.leadInitials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium tracking-[-0.26px] text-[#1e302a] truncate">
                    {active.leadName}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {active.stage && (
                      <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-[#edf1ff] text-[#335CFF]">
                        {active.stage}
                      </span>
                    )}
                    {active.phone && (
                      <span className="text-[10px] font-normal text-[#697571]">
                        {active.phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Note */}
            <div className="px-4 py-3">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px] font-medium text-[#697571] uppercase tracking-[0.04em]">
                  Note
                </p>
                <button
                  onClick={() => {
                    // Commit on "Done". Without this the edit only lived in
                    // local state, so Postpone (which reads `active.note` once
                    // editing is off) silently wrote the note the user had just
                    // replaced.
                    if (editingNote) saveNoteIfEditing();
                    setEditingNote(!editingNote);
                  }}
                  className="text-[10px] font-medium text-[#218760] hover:text-[#1a6b4c] transition-colors"
                >
                  {editingNote ? "Done" : "Edit"}
                </button>
              </div>
              {editingNote ? (
                <textarea
                  value={localNote}
                  onChange={(e) => setLocalNote(e.target.value)}
                  autoFocus
                  className="w-full p-2.5 rounded-xl border border-[#e6e8e7] bg-white text-[12px] text-[#1e302a] tracking-[-0.24px] resize-none min-h-[60px] focus:outline-none focus:ring-1 focus:ring-[#218760] focus:border-[#218760] placeholder:text-[#9ba3a0] transition-all"
                />
              ) : (
                <p className="text-[12px] font-normal tracking-[-0.24px] text-[#374741] leading-[1.5]">
                  {active.note || (
                    <span className="text-[#9ba3a0] italic">No note added</span>
                  )}
                </p>
              )}
            </div>

            {/* Postpone */}
            <div className="px-4 pb-3">
              <p className="text-[10px] font-medium text-[#697571] uppercase tracking-[0.04em] mb-2">
                Postpone
              </p>
              <div className="flex gap-1.5">
                {(followUpTimeEnabled
                  ? TIMED_OPTIONS
                  : DATE_ONLY_OPTIONS
                ).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => handlePostpone(opt)}
                    className="flex-1 flex items-center justify-center gap-1 px-2.5 py-2 rounded-xl border border-[#e6e8e7] bg-white hover:bg-[#f7f9f8] hover:border-[#d6ebe3] transition-all"
                  >
                    <Clock className="size-3 text-[#697571]" />
                    <span className="text-[11px] font-medium tracking-[-0.22px] text-[#374741]">
                      {POSTPONE_LABEL[opt]}
                    </span>
                  </button>
                ))}
              </div>
              {followUpTimeEnabled && (
                <p className="text-[9px] font-normal text-[#9ba3a0] mt-1 text-center">
                  Tomorrow reschedules to 10:00 AM
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 px-4 pb-4">
              <button
                onClick={handleViewLead}
                className="flex-1 flex items-center justify-center gap-1.5 h-9 rounded-xl bg-[#218760] hover:bg-[#1a6b4c] text-white transition-colors"
              >
                <Eye className="size-3.5" />
                <span className="text-[12px] font-medium tracking-[-0.24px]">
                  View Lead
                </span>
                <ChevronRight className="size-3" />
              </button>
              <button
                onClick={() => {
                  const url = buildWhatsAppUrl(
                    active.phone,
                    active.phoneCountryCode ?? undefined,
                  );
                  if (url) window.open(url, "_blank");
                }}
                className="size-9 shrink-0 rounded-xl border border-[#e6e8e7] flex items-center justify-center hover:bg-[#eaf5f1] hover:border-[#d6ebe3] transition-all"
              >
                <MessageCircle className="size-3.5 text-[#25D366]" />
              </button>
              <button
                onClick={() => {
                  const href = buildTelHref(
                    active.phone,
                    active.phoneCountryCode ?? undefined,
                  );
                  if (href) window.open(href, "_self");
                }}
                className="size-9 shrink-0 rounded-xl border border-[#e6e8e7] flex items-center justify-center hover:bg-[#eaf5f1] hover:border-[#d6ebe3] transition-all"
              >
                <Phone className="size-3.5 text-[#218760]" />
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
